import React from 'react';
import {
  TableColumn,
  Link,
} from '@backstage/core-components';
import {
  EntityOwnerPicker,
  EntityTagPicker,
  EntityTypePicker,
  UserListPicker,
  EntityKindPicker,
} from '@backstage/plugin-catalog-react';
import { CatalogTableRow } from '@backstage/plugin-catalog';
import { Chip, Tooltip } from '@material-ui/core';
import { CustomLifecyclePicker, CustomSystemPicker, CustomNamespacePicker } from './CustomFilters';

/**
 * Custom columns that use PhysicsX terminology with proper link rendering
 * - "Lifecycle" is displayed as "Ring"
 * - "System" is displayed as "Stamp"
 */
export const createCustomColumns = (): TableColumn<CatalogTableRow>[] => {
  return [
    // Name column with link
    {
      title: 'Name',
      field: 'resolved.entityRef',
      highlight: true,
      cellStyle: {
        whiteSpace: 'nowrap',
      },
      customSort: ({ entity: entity1 }, { entity: entity2 }) => {
        const name1 = entity1.metadata?.title || entity1.metadata.name;
        const name2 = entity2.metadata?.title || entity2.metadata.name;
        return name1.localeCompare(name2);
      },
      render: ({ entity }) => {
        const kind = entity.kind.toLowerCase();
        const namespace = entity.metadata.namespace || 'default';
        const name = entity.metadata.name;
        
        // For components: show namespace/name format
        // For other kinds (system, etc.): show just the name
        let displayName = entity.metadata.title || name;
        if (kind === 'component' && namespace !== 'default') {
          displayName = entity.metadata.title || `${namespace}/${name}`;
        }
        
        return (
          <Link to={`/catalog/${namespace}/${kind}/${name}`}>
            {displayName}
          </Link>
        );
      },
    },
    // Stamp column (formerly System) with links
    {
      title: 'Stamp',
      field: 'resolved.partOfSystemRelationTitle',
      cellStyle: {
        whiteSpace: 'nowrap',
      },
      render: ({ resolved }) => {
        if (!resolved.partOfSystemRelations || resolved.partOfSystemRelations.length === 0) {
          return null;
        }
        return (
          <>
            {resolved.partOfSystemRelations.map((ref, index) => {
              const namespace = ref.namespace || 'default';
              const name = ref.name;
              return (
                <React.Fragment key={`${namespace}/${name}`}>
                  {index > 0 && ', '}
                  <Link to={`/catalog/${namespace}/system/${name}`}>
                    {name}
                  </Link>
                </React.Fragment>
              );
            })}
          </>
        );
      },
    },
    // Owner column with links
    {
      title: 'Owner',
      field: 'resolved.ownedByRelationsTitle',
      cellStyle: {
        whiteSpace: 'nowrap',
      },
      render: ({ resolved }) => {
        if (!resolved.ownedByRelations || resolved.ownedByRelations.length === 0) {
          return null;
        }
        return (
          <>
            {resolved.ownedByRelations.map((ref, index) => {
              const namespace = ref.namespace || 'default';
              const kind = ref.kind.toLowerCase();
              const name = ref.name;
              return (
                <React.Fragment key={`${namespace}/${kind}/${name}`}>
                  {index > 0 && ', '}
                  <Link to={`/catalog/${namespace}/${kind}/${name}`}>
                    {name}
                  </Link>
                </React.Fragment>
              );
            })}
          </>
        );
      },
    },
    // Type column
    {
      title: 'Type',
      field: 'entity.spec.type',
      cellStyle: {
        whiteSpace: 'nowrap',
      },
    },
    // Ring column (formerly Lifecycle)
    {
      title: 'Ring',
      field: 'entity.spec.lifecycle',
      cellStyle: {
        whiteSpace: 'nowrap',
      },
    },
    // Version column from annotation
    {
      title: 'Version',
      field: 'entity.metadata.annotations',
      cellStyle: {
        whiteSpace: 'nowrap',
      },
      customSort: ({ entity: entity1 }, { entity: entity2 }) => {
        const version1 = entity1.metadata.annotations?.['physicsx.ai/version'] || '';
        const version2 = entity2.metadata.annotations?.['physicsx.ai/version'] || '';
        return version1.localeCompare(version2);
      },
      render: ({ entity }) => {
        const version = entity.metadata.annotations?.['physicsx.ai/version'];
        if (!version) {
          return null;
        }

        return (
          <Tooltip title={version} arrow>
            <span>{version.slice(0, 7)}</span>
          </Tooltip>
        );
      },
    },
    // Tags column with chips
    {
      title: 'Tags',
      field: 'entity.metadata.tags',
      cellStyle: {
        padding: '4px 8px',
      },
      render: ({ entity }) => (
        <>
          {entity.metadata.tags?.slice(0, 3).map(tag => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant="outlined"
              style={{ marginRight: '4px', marginBottom: '2px' }}
            />
          ))}
          {entity.metadata.tags && entity.metadata.tags.length > 3 && (
            <Chip
              label={`+${entity.metadata.tags.length - 3}`}
              size="small"
              variant="outlined"
              style={{ marginBottom: '2px' }}
            />
          )}
        </>
      ),
    },
  ];
};

/**
 * Custom filters component for the catalog page
 * Uses PhysicsX terminology (Ring instead of Lifecycle, Stamp instead of System)
 */
export const CustomFilters = () => (
  <>
    <EntityKindPicker />
    <EntityTypePicker />
    <UserListPicker />
    <EntityOwnerPicker />
    <CustomLifecyclePicker />
    <CustomSystemPicker />
    <EntityTagPicker />
    <CustomNamespacePicker />
  </>
);
